/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useCallback, useEffect } from 'react';

import type { ApolloError } from 'apollo-client';

import type { CourseItem, Product } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachChat, type CoachChatProps } from 'bundles/ai-coach-platform/components/patterns';
import {
  addResponseMessage,
  dropMessages,
  enableMsgLoader,
  sendMessage as sendMessagePlatform,
  setChatSessionId,
} from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import type { CoachModeType } from 'bundles/ai-coach-platform/types';
import EmptyState from 'bundles/ai-course-coach/components/HomeView/EmptyState';
import WelcomeBanner from 'bundles/ai-course-coach/components/PersonalizedWelcome/WelcomeBanner';
import { getErrorMessageText } from 'bundles/ai-course-coach/components/constants';
import { getCoachPendoTrigger } from 'bundles/ai-course-coach/utils/pendo';
import { isContinuousChatEnabled, isIntentionRoutingEnabled } from 'bundles/ai-course-coach/utils/productFeatureFlags';
import { captureMessageInSentry } from 'bundles/ai-course-coach/utils/sentryUtils';
import type { HomeView as HomeViewType } from 'bundles/ai-course-coach/utils/types';
import { useCourseToolBarPlugins } from 'bundles/course-v2/components/navigation/CourseToolBar';

export type Props = {
  itemId?: string;
  courseId?: string;
  mode: CoachModeType;
  homeView?: HomeViewType;
  onClose?: CoachChatProps['onClose'];
  onMount?: CoachChatProps['onMount'];
  disableSpeechToText?: CoachChatProps['disableSpeechToText'];
  speechToTextDisabledTooltip?: CoachChatProps['speechToTextDisabledTooltip'];
  renderTemporaryMessage?: CoachChatProps['renderTemporaryMessage'];
  /**
   * Whether to show the personalized welcome banner.
   */
  showPersonalizedWelcome?: boolean;
  /**
   * Function to clear the show personalized welcome state.
   */
  clearShowPersonalizedWelcome?: () => void;
};

const styles = {
  additionalStyles: css`
    padding-left: 0;

    .coach-message-loader:first-of-type {
      animation: slideInFromTop 0.4s ease-in-out;

      @keyframes slideInFromTop {
        0% {
          opacity: 0;
          transform: translateY(-10%);
        }

        30% {
          opacity: 0.2;
        }

        100% {
          opacity: 1;
          transform: translateY(0%);
        }
      }
    }
  `,
};

/**
 * Main component for the "Coach Tutor" experience, that is shown on several of our learning experience pages for
 * learners to interact with Coach AI.
 */
const CoachChatEmbedded = ({
  itemId,
  courseId,
  mode,
  homeView,
  onClose,
  onMount,
  disableSpeechToText = false,
  speechToTextDisabledTooltip = '',
  renderTemporaryMessage = () => null,
  showPersonalizedWelcome = false,
  clearShowPersonalizedWelcome = () => null,
}: Props) => {
  const { expanded: coachChatOpen } = useCourseToolBarPlugins();

  const track = useTracker();

  useEffect(() => {
    return () => {
      // clean up some store state and start fresh for the next mount
      // which may be on the same or a different page
      dropMessages(); // clear messages, these will be refetched on mount anyway
      // unset sessionId in store, will be reset on mount
      // if not cleared we may show data for the wrong page/session
      setChatSessionId(undefined);
    };
  }, []);

  const handleError = (apolloError: ApolloError) => {
    enableMsgLoader(false);
    addResponseMessage({ text: getErrorMessageText() });
    if (apolloError.graphQLErrors && apolloError.graphQLErrors?.length > 0) {
      const graphQLError = apolloError.graphQLErrors[0];
      if (graphQLError) {
        captureMessageInSentry({ error: graphQLError, source: 'sendMessage', coachMode: mode });
      }
    }
  };

  const handleClose = () => {
    onClose?.();

    // used to notify ExternalCoachLauncher when Coach is closed
    document.body.dispatchEvent(new CustomEvent('coachChatClosed'));

    track('interact_coach', {
      coachInteractionType: 'close_coach_tool',
      coach: {
        mode,
        location: 'toolbar',
      },
      ...(courseId && {
        product: {
          type: 'course',
          id: courseId,
        } as Product,
      }),
      ...(itemId && {
        courseItem: {
          id: itemId,
        } as CourseItem,
      }),
    });
  };

  const coachPendoTriggerInstance = getCoachPendoTrigger({ courseId });

  const handlePendoTriggerMessage = () => {
    // for each message sent, check if we need to show the pendo guide
    coachPendoTriggerInstance.handleNewMessage();
  };

  const handlePendoTriggerOpen = useCallback<() => void>(() => {
    // for each time chat is open, check if we need to show the pendo guide
    coachPendoTriggerInstance.handleOpenChat();
  }, [coachPendoTriggerInstance]);

  useEffect(() => {
    if (coachChatOpen) {
      handlePendoTriggerOpen();
    }
  }, [coachChatOpen, handlePendoTriggerOpen]);

  const handleSend = () => {
    handlePendoTriggerMessage();
  };

  const handleHideMessage = ({ index }: { index: number }) => {
    // for Personalized Welcome experience, hide the first learner message so it looks like Coach initiated the chat
    return showPersonalizedWelcome && index === 0;
  };

  const handleStartNewChat = () => {
    if (showPersonalizedWelcome) {
      clearShowPersonalizedWelcome();

      // TODO: add here the mutation to update the personalized welcome event type
    }
  };

  const contextConfig = { courseraContext: { courseId, itemId } };
  const enableContinuousChat = isContinuousChatEnabled({ epicImpression: true });
  const chatPrefixContent = showPersonalizedWelcome ? <WelcomeBanner /> : null;

  return (
    <div data-testid="coursera-coach-widget">
      <CoachChat
        enableTextStreaming={true}
        enableContinuousChat={enableContinuousChat}
        enableFindInChat={enableContinuousChat}
        enableRichInputField={true}
        additionalStyles={styles.additionalStyles}
        showSettings={true}
        trackingData={{
          mode,
          location: 'toolbar',
          courseId,
          itemId,
        }}
        onSend={handleSend}
        onMount={onMount}
        onStartNewChat={handleStartNewChat}
        hideMessage={handleHideMessage}
        emptyState={
          <EmptyState
            sendMessage={(message: string, action: string) => {
              handlePendoTriggerMessage();
              sendMessagePlatform({
                input: {
                  message,
                  config: {
                    coachActionId: action,
                    coachModeId: mode,
                  },
                  ...contextConfig,
                },
                enableTextStreaming: true,
                enableContinuousChat,
              }).catch(handleError);
            }}
            quickMessages={homeView?.quickMessages}
            title={homeView?.title}
            subtitle={homeView?.subtitle}
          />
        }
        chatPrefixContent={chatPrefixContent}
        suggestedActions={homeView?.quickMessages?.map((action) => ({
          key: action.action,
          label: action.message,
          onClick: () => {
            sendMessagePlatform({
              input: {
                message: action.message,
                config: {
                  coachActionId: action.action,
                  coachModeId: mode,
                },
                ...contextConfig,
              },
              enableTextStreaming: true,
              enableContinuousChat,
            }).catch(handleError);

            track('interact_coach', {
              coachInteractionType: 'click_action',
              coach: {
                mode,
                location: 'suggested_actions_chat_popover',
              },
              coachAction: action.action,
              ...(courseId && {
                product: {
                  type: 'course',
                  id: courseId,
                },
              }),
              ...(itemId && {
                courseItem: {
                  id: itemId,
                },
              }),
            });
          },
        }))}
        mode={mode}
        contextConfig={contextConfig}
        onClose={handleClose}
        disableSpeechToText={disableSpeechToText}
        speechToTextDisabledTooltip={speechToTextDisabledTooltip}
        enableIntentionRouting={isIntentionRoutingEnabled()}
        renderTemporaryMessage={renderTemporaryMessage}
        chatHistoryLimit={enableContinuousChat ? 20 : 5}
      />
    </div>
  );
};

CoachChatEmbedded.displayName = 'CoachChatEmbedded';

export default CoachChatEmbedded;
